import axios, { CancelTokenSource } from "axios";
import router from "@/router";
import { ClientProject } from "@/models/ClientProject";
import { Lead, ClientEditViewModel, LeadEditViewModel, LeadsPageViewModel, UpdateLeadResponse, PhonePopupData } from "@/models/leads/Lead";
import { DataSourceResult } from "@/models/DataSourceResult";
import { ExplanationPopupData, ExplanationRequestData, ExplanationSendEmailRequest, ExplanationSaveImageFileRequest } from "@/models/ExplanationPopupData";
import { GridColumn, GridColumnExportSetting } from "@/models/GridColumn";
import {LeadsPageKind} from "@/models/LeadsPageKind";
import { AdTitlesByLeads, Assignee, ChannelMultiselectItem, MultiselectItem, SelectListItem } from "@/models/MultiselectItem";
import { Result } from "@/models/Result";
import { TableSettingsResponse, TableSettingsSaveRequestData } from "@/models/TableSettings";
import { UserData } from "@/models/UserData";
import { ChannelType } from "@/models/campaigns/ChannelType";
import { LeadStatistic, LeadStatisticExtended, PhoneLeadStats } from "@/models/leads/LeadStatistic";
import { PhoneStatus } from "@/models/phones/PhoneWithChannel";
import ConfigService from "../ConfigService";
import HelperService from "../HelperService";

export const requestConfig = {
  withCredentials: true,
};

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error instanceof Error) {
      if ((error as Error).message === "Network Error") {
        HelperService.showError(`${(error as Error).message} | ${(error as any).config?.url}`);
        return false;
      }
    }
    return Promise.reject(error);
  }
);

export default {
  deleteLead(id: number): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.deleteLeadUrl);
      const config = {
        ...requestConfig,
        params: {
          Id: id
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve({
            Message: response.data.msg,
            Success: response.data.success
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  exportLeadsToExcel(leads: DataSourceResult, columns: GridColumn[], kind : LeadsPageKind): Promise<string> {
    const columnsToExport: GridColumnExportSetting[] = columns.map((i: GridColumn) => {
      return {
        Field: i.name,
        Forbidden: i.forbidden,
        Width: i.width ? i.width.toString() : "",
        Hidden: !i.visible,
      };
    });
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.exportLeadsToExcelUrl);
      axios
        .post(url.toString(), { Columns: columnsToExport, Leads: JSON.parse(JSON.stringify(leads)).Data, ShowClients: kind == LeadsPageKind.Clients ? "true" : "false" }, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
    },

    exportLeadsToExcelToMail(leadsPayload: LeadsPayload, columns: GridColumn[], kind: LeadsPageKind): Promise<string> {
        const columnsToExport: GridColumnExportSetting[] = columns.map((i: GridColumn) => {
            return {
                Field: i.name,
                Forbidden: i.forbidden,
                Width: i.width ? i.width.toString() : "",
                Hidden: !i.visible,
            };
        });
        return new Promise((resolve, reject) => {
            const url = new URL(ConfigService.exportLeadsToExcelToMailUrl);
               
            if (leadsPayload.filter) {
                url.searchParams.append("filter", leadsPayload.filter);
            }
            if (leadsPayload.group) {
                url.searchParams.append("group", leadsPayload.group);
            }
            if (leadsPayload.page) {
                url.searchParams.append("page", leadsPayload.page.toString());
            }
            if (leadsPayload.pageSize) {
                url.searchParams.append("pageSize", leadsPayload.pageSize.toString());
            }
            if (leadsPayload.sort) {
                url.searchParams.append("sort", leadsPayload.sort);
            }

            axios
                .post(url.toString(), { Columns: columnsToExport, ShowClients: kind == LeadsPageKind.Clients ? "true" : "false", LeadsPayload: leadsPayload }, requestConfig)
                .then((response) => {
                    const result: Result = response.data;
                    if (result.Success) {
                        resolve(result.Data);
                    } else {
                        reject(result.Message);
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

  getAdGroups(payload: CommonMultiselectPayload): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.adGroupsUrl);
      url.searchParams.append("companyIds", JSON.stringify(payload.companyIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAdPreviewData(adId: number, adGroupId: number, channelId: number): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.getAdPreviewDataUrl);
      const config = {
        ...requestConfig,
        params: {
          aid: adId,
          agroupId: adGroupId,
          channelid: channelId
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAdTitles(payload: CommonMultiselectPayload): Promise<AdTitlesByLeads[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.adTitlesUrl);
      url.searchParams.append("companyIds", JSON.stringify(payload.companyIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAdditionalStatuses(payload: CommonMultiselectPayload): Promise<SelectListItem[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.additionalStatusesUrl);
      url.searchParams.append("companyIds", JSON.stringify(payload.companyIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAssets(payload: CommonMultiselectPayload): Promise<ClientProject[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.assetsUrl);
      url.searchParams.append("companyIds", JSON.stringify(payload.companyIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAssignees(payload: CommonMultiselectPayload): Promise<Assignee[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.assigneesUrl);
      url.searchParams.append("companyIds", JSON.stringify(payload.companyIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data.Data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getAssigneesForClient(payload: AssigneesForClientPayload): Promise<Assignee[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.assigneesUrl);
      if (payload.companyId) {
        url.searchParams.append("companyId", payload.companyId.toString());
      }
      if (payload.assigneeId) {
        url.searchParams.append("id", payload.assigneeId.toString());
      }
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data.Data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getChannels(payload: CommonMultiselectPayload): Promise<ChannelMultiselectItem[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.channelsUrl);
      url.searchParams.append("companyIds", JSON.stringify(payload.companyIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getCompanies(): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      axios
        .post(`${ConfigService.companiesUrl}?addShared=True`, null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getProjects(payload: CommonMultiselectPayload): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.projectsUrl);
      url.searchParams.append("companyIds", JSON.stringify(payload.companyIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getProcesses(companyId: number): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.processesUrl);
      url.searchParams.append("companyId", companyId.toString());
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getDataWithLeadsPayload(payload: LeadsPayload, urlString: string, cancelTokenSource?: CancelTokenSource): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = new URL(urlString);
      if (payload.companyId) {
        url.searchParams.append("companyId", payload.companyId.toString());
      }
      if (payload.dateFrom) {
        url.searchParams.append("DateFrom", payload.dateFrom.toString());
      }
      if (payload.datePeriod) {
        url.searchParams.append("DatePeriod", payload.datePeriod);
      }
      if (payload.dateTo) {
        url.searchParams.append("DateTo", payload.dateTo.toString());
      }
      if (payload.showDeleted !== undefined) {
        url.searchParams.append("showDeleted", payload.showDeleted ? "true" : "false");
      }
      if (payload.filter) {
        url.searchParams.append("filter", payload.filter);
      }
      if (payload.group) {
        url.searchParams.append("group", payload.group);
      }
      if (payload.page) {
        url.searchParams.append("page", payload.page.toString());
      }
      if (payload.pageSize) {
        url.searchParams.append("pageSize", payload.pageSize.toString());
      }
      if (payload.priceType) {
        url.searchParams.append("priceType", payload.priceType);
      }
      if (payload.sort) {
        url.searchParams.append("sort", payload.sort);
      }
      if (payload.token) {
        url.searchParams.append("token", payload.token);
      }
      if (payload.unique !== undefined) {
        url.searchParams.append("unique", payload.unique ? "true" : "false");
      }
      if (payload.kind !== undefined) {
        url.searchParams.append("showclients", payload.kind == LeadsPageKind.Clients  ? "true" : "false");
      }
      if (payload.exportFlag) {
        url.searchParams.append("exportFlag", payload.exportFlag.toString());
      }
      const config = {
        ...requestConfig,
        cancelToken: cancelTokenSource?.token,
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            reject(error);
          }
        });
    });
  },

  

  getDataWithLeadsStatisticByPhonesPayload(payload: LeadsStatisticPhonesPayload, urlString: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = new URL(urlString);
      if (payload.dateFrom) {
        url.searchParams.append("dateFrom", payload.dateFrom.toString());
      }
      if (payload.dateTo) {
        url.searchParams.append("dateTo", payload.dateTo.toString());
      }
      if (payload.filter) {
        url.searchParams.append("filter", payload.filter);
      }
      if (payload.phoneStatus) {
        url.searchParams.append("phoneStatus", payload.phoneStatus.toString());
      }
      if (payload.token) {
        url.searchParams.append("token", payload.token);
      }
      if (payload.channelMetadataTypes) {
        payload.channelMetadataTypes.forEach((i: ChannelType) => {
          url.searchParams.append("channelMetadataTypes", i.toString());
        })
      }

      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getExplanationPopup(): Promise<ExplanationPopupData | undefined> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.explanationPopupUrl);
      const requestData: ExplanationRequestData = {
        url: location.href
      };
      if (typeof((window as any).fixdigital_explanation) !== undefined) { 
        const explanation: ExplanationRequestData = (window as any).fixdigital_explanation;
        if (explanation && explanation.username) {
          requestData.username = explanation.username;
        }
        if (explanation && explanation.locale) {
          requestData.locale = explanation.locale;
        }
      }
      const config = {
        ...requestConfig,
        params: {
          locale: requestData.locale,
          url: requestData.url,
          username: requestData.username,
        }
      };
      axios
        .post(url.toString(), null, config).then((response) => {
          if (!response?.data) {
            resolve(undefined);
          }
          resolve(response.data);
        }).catch((error) => {
          reject(error);
        });
    });
  },

  getClientEditViewModel(id: number): Promise<ClientEditViewModel> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.clientUrl);
      const config = {
        ...requestConfig,
        params: {
          id
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            switch (result.Message) {
              case "404":
                router.push({ name: "NotFound"});
                break;
              default:
                reject(result.Message);
                break;
            }
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getLeadEditViewModel(id: number): Promise<LeadEditViewModel> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.leadUrl);
      const config = {
        ...requestConfig,
        params: {
          id
        }
      };
      axios
        .post(url.toString(), null, config)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            switch (result.Message) {
              case "404":
                router.push({ name: "NotFound"});
                break;
              default:
                reject(result.Message);
                break;
            }
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getLeads(payload: LeadsPayload, cancelTokenSource?: CancelTokenSource): Promise<DataSourceResult> {
    return this.getDataWithLeadsPayload(payload, ConfigService.contactFormsUrl, cancelTokenSource);
  },

  getLeadsPageData(): Promise<LeadsPageViewModel> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.leadsPageDataUrl);
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

    checkPhonePopup(): Promise<PhonePopupData> {
        const url = new URL(ConfigService.checkPhonePopupUrl);

        return axios
            .get(url.toString())
            .then((response) => {
                const result = response.data;
                if (result && typeof result.showPopup === "boolean") {
                    return result;
                } else {
                    return Promise.reject("Invalid server response.");
                }
            })
            .catch((error) => {
                console.error("Error in CheckPhonePopup:", error);
                return Promise.reject(error);
            });
    },

    setHidePopupCookie(): Promise<void> {
        const url = new URL(ConfigService.setHidePopupCookieUrl);

        return axios
            .post(url.toString())
            .then((response) => {
                if (response.status === 200) {
                    return Promise.resolve();
                } else {
                    return Promise.reject("Failed to set hide popup cookie.");
                }
            })
            .catch((error) => {
                console.error("Error in SetHidePopupCookie:", error);
                return Promise.reject(error);
            });
    },


  

  getLeadsStatisticsAll(payload: LeadsPayload, cancelTokenSource?: CancelTokenSource): Promise<{
    additionalStatus: LeadStatistic[];
    callStatus: LeadStatistic[];
    phoneAndForms: LeadStatistic[];
    saleStatus: LeadStatistic[];
    campaigns: LeadStatistic[];
    dealMeetingsLeads: LeadStatisticExtended[];
    channelMetadataType?: LeadStatistic[];
  }> {
    return this.getDataWithLeadsPayload(payload, ConfigService.leadsStatisticAllUrl, cancelTokenSource);
  },

  getLeadsStatisticPhones(payload: LeadsStatisticPhonesPayload): Promise<PhoneLeadStats> {
    return this.getDataWithLeadsStatisticByPhonesPayload(payload, ConfigService.leadsStatisticCallsByDateUrl);
  },

  getLeadHistory(clientID: number, pageKind: LeadsPageKind = LeadsPageKind.Leads): Promise<DataSourceResult> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.leadHistoryUrl);
      url.searchParams.append("id", clientID.toString());
      url.searchParams.append("pageKind", pageKind.toString());
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getTableSettings(culture: string, pageUrl: string): Promise<TableSettingsResponse> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.tableSettingsUrl);
      url.searchParams.append("culture", culture);
      url.searchParams.append("url", pageUrl);
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getTags(payload: CommonMultiselectPayload): Promise<MultiselectItem[]> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.tagsUrl);
      url.searchParams.append("companyIds", JSON.stringify(payload.companyIds));
      url.searchParams.append("showNotActiveCamp", payload.showNotActiveCamp.toString());
      url.searchParams.append("metadataIds", JSON.stringify(payload.metadataIds));
      url.searchParams.append("statusIds", JSON.stringify(payload.statusIds));
      url.searchParams.append("channelIds", JSON.stringify(payload.channelIds));
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  getUserData(): Promise<UserData | undefined> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.userDataUrl);
      axios
        .post(url.toString(), null, requestConfig)
        .then((response) => {
          if (!response?.data) {
            const currentPath = location.pathname + location.search;
            window.location.replace(`${ConfigService.loginUrl}?returnUrl=${encodeURIComponent(currentPath)}`);
            resolve(undefined);
            return;
          }

          if (typeof(response.data) === "string") {
            const currentPath = location.pathname + location.search;
            window.location.replace(`${ConfigService.loginUrl}?returnUrl=${encodeURIComponent(currentPath)}`);
            resolve(undefined);
            return;
          }

          const result: Result = response.data;
          if (result.Success) {
            resolve(result.Data);
          } else {
            reject(result.Message);
          }
        })
        .catch((error) => {
          reject(error);
          const currentPath = location.pathname + location.search;
          window.location.replace(`${ConfigService.loginUrl}?returnUrl=${encodeURIComponent(currentPath)}`);
        });
    });
  },

  logout(): Promise<void> {
    return new Promise((resolve, reject) => {
      axios
        .get(ConfigService.logoutUrl, requestConfig)
        .then((response) => {
          const result: Result = response.data;
          if (result.Success) {
            resolve();
          } else {
            HelperService.showError(result.Message);
            reject(result.Message);
          }
        })
        .catch((error) => {
          HelperService.showError(error);
          reject(error);
        });
    });
  },

  saveExplanationModuleImage(data: ExplanationSaveImageFileRequest): Promise<Result>{
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.explanationSaveImageFile);
      const formData = new FormData();
      formData.append("image", data.image);
      formData.append("imagename", data.imagename);
      const uploadConfig = { ...requestConfig, maxContentLength: Infinity, maxBodyLength: Infinity };
      axios
        .post(url.toString(), formData, uploadConfig).then((response) => {
          resolve({
            Data : response.data.url,
            Message: response.data.message,
            Success: response.data.success
          });
        }).catch((error) => {
          reject(error);
        });
    });
  },

  sendExplanationModuleEmail(data: ExplanationSendEmailRequest): Promise<Result> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.explanationSendEmailUrl);
      url.searchParams.append("email", data.email);
      url.searchParams.append("message", data.message);
      url.searchParams.append("name", data.name);
      url.searchParams.append("phone", data.phone);
      url.searchParams.append("url", data.url);
      url.searchParams.append("username", data.username);
      url.searchParams.append("subject", data.subject);
      axios
        .post(url.toString(), null, requestConfig).then((response) => {
          resolve({
            Message: response.data.message,
            Success: response.data.success
          });
        }).catch((error) => {
          reject(error);
        });
    });
    },

    sendExplanationModuleAlgoliaPost(subject: string): Promise<Result> {
        return new Promise((resolve, reject) => {
            const url = new URL(ConfigService.explanationSendAlgoliaPost);
            url.searchParams.append("subject", subject);
            axios
                .post(url.toString(), null, requestConfig).then((response) => {
                    resolve({
                        Data: response.data.result,
                        Message: response.data.message,
                        Success: response.data.success
                    });
                }).catch((error) => {
                    reject(error);
                });
        });
    },

  setLanguage(culture: string): Promise<UserData> {
    return new Promise((resolve, reject) => {
      const url = ConfigService.setLanguageUrl;

      const config = {
        ...requestConfig,
        params: {
          culture,
          returnUrl: location.pathname
        }
      };
    axios
      .post(url, null, config)
      .then(() => {
        location.reload();
      })
      .catch((error) => {
        console.error('Error setting language:', error);
        location.reload();
      });
    });
  },

  saveTableSettings(data: TableSettingsSaveRequestData): Promise<Result> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("culture", data.culture);
      formData.append("gridId", data.gridId);
      formData.append("optionsJson", data.optionsJson);
      formData.append("url", data.url);
      const url = new URL(ConfigService.saveTableSettingsUrl);
      axios
        .post(url.toString(), formData, requestConfig)
        .then((response) => {
          resolve({
            Message: response?.data?.message || "",
            Success: response?.data?.success,
          });
        })
        .catch((error) => {
          // If backend provided a message, surface it to callers
          if (error?.response?.data) {
            resolve({
              Message: error.response.data.message || error.response.data.Message || "Failed to save table settings.",
              Success: error.response.data.success ?? false,
            });
            return;
          }

          reject(error);
        });
    });
  },

  updateLead(lead: Lead): Promise<UpdateLeadResponse> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.updateLeadUrl);
      axios
        .post(url.toString(), lead, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  updateClient(client: Lead): Promise<UpdateLeadResponse> {
    return new Promise((resolve, reject) => {
      const url = new URL(ConfigService.updateClientUrl);
      axios
        .post(url.toString(), client, requestConfig)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};

export interface AssigneesForClientPayload {
  assigneeId?: number;
  companyId?: number;
}

export interface CommonMultiselectPayload {
  companyIds: number[];
  showNotActiveCamp: boolean;
  metadataIds: number[];
  statusIds: string[];
  channelIds: number[];
}

export interface LeadsPayload {
  companyId?: number;
  dateFrom?: string;
  datePeriod?: string;
  dateTo?: string;
  filter?: string;
  group?: string;
  kind?: LeadsPageKind;
  page?: number;
  pageSize: number;
  priceType?: string;
  showDeleted?: boolean;
  sort?: string;
  token?: string;
  unique?: boolean;
  exportFlag?: boolean;
}

export interface LeadsStatisticPhonesPayload {
  dateFrom?: string;
  dateTo?: string;
  filter?: string;
  pageSize: number;
  phoneStatus: PhoneStatus;
  token?: string;
  channelMetadataTypes?: ChannelType[];
}

 
